import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createUser,
  loginUser,
  loginWithGoogle,
  logOut,
  onAuthStateChangedListener,
  registerFCMToken,
  sendPasswordReset,
} from "../firebase/firebaseAuth"; // Import helper functions
import { analytics, logEvent, firestore } from "../firebase/firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";
import { awardFreeTradesAfterVerification } from "../services/userService"; // Ensure this function is available

// Create the AuthContext
export const AuthContext = createContext();

// List of public routes
const PUBLIC_ROUTES = ["/", "/auth/sign-in", "/auth/sign-up", "/auth/forgot-password", "/test", "/terms-of-use", "/privacy-policy", "/account-deletion"];

// **AuthProvider Component**
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store the authenticated user
  const [firebaseUser, setFirebaseUser] = useState(null); // Store the authenticated user
  const [loading, setLoading] = useState(true); // Track the loading state
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("AuthProvider mounted");

    let unsubscribeAuth;
    let unsubscribeUserDoc;

    // Listen to authentication state changes
    unsubscribeAuth = onAuthStateChangedListener(async (currentUser) => {
      setLoading(true);
      try {
        if (currentUser) {
          console.log("User detected:", currentUser);
          setFirebaseUser(currentUser); // Store Firebase user object

          // Set basic user details
          const userData = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || "Anonymous",
          };
          setUser(userData);

          // Log event in analytics
          logEvent(analytics, "session_verified", { email: currentUser.email });

          // Register the FCM Token for push notifications
          await registerFCMToken(currentUser.uid);

          // **Set up Firestore real-time listener for user verification status**
          const userDocRef = doc(firestore, "users", currentUser.uid);
          unsubscribeUserDoc = onSnapshot(userDocRef, async (docSnapshot) => {
            if (docSnapshot.exists()) {
              const userData = docSnapshot.data();
              console.log("User document updated:", userData);

              // Update the user state with Firestore data
              setUser((prevUser) => ({ ...prevUser, ...userData }));

              // Check and award free trades if the user has completed verification
              if (
                userData.emailVerified &&
                userData.phoneVerified &&
                userData.isProfileUpdated &&
                userData.kycVerified
              ) {
                await awardFreeTradesAfterVerification(currentUser.uid);
              }
            }
          });
        } else {
          console.warn("No active session.");

          // Redirect only if the current route is not public
          if (!PUBLIC_ROUTES.includes(location.pathname)) {
            console.warn("Redirecting to sign-in.");
            navigate("/auth/sign-in");
          }
          setUser(null);
          setFirebaseUser(null);
        }
      } catch (error) {
        console.error("Error during auth state change:", error);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup listeners when component unmounts
    return () => {
      console.log("AuthProvider unmounted. Cleaning up...");
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, [navigate, location.pathname]);

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        createUser,
        loginUser,
        loginWithGoogle,
        logOut,
        sendPasswordReset,
      }}
    >
      {loading ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <p>Loading session...</p>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// **Custom Hook for Auth Context**
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
